import { ChevronDown } from 'lucide-react';
import type { ComponentProps } from 'react';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@vs-shop/ui/collapsible';
import { clsxm } from '@vs-shop/utils';

import { optionalClassName } from '../../internal/optionalClassName.ts';

/**
 * The band folded: its title stays, the rest of it is behind a control.
 *
 *   const { isOpen, setOpen, triggerRef } =
 *     useProductBandDisclosure({ anchor: 'description', defaultOpen: true });
 *
 *   <ProductBandDisclosure open={isOpen} onOpenChange={setOpen}>
 *     <ProductBandDisclosureTrigger ref={triggerRef}>
 *       <ProductBandDisclosureHeader>
 *         <ProductBandTitle render={<Heading level={2} />}>{title}</ProductBandTitle>
 *         <ProductBandDisclosureChevron />
 *       </ProductBandDisclosureHeader>
 *     </ProductBandDisclosureTrigger>
 *     <ProductBandDisclosureContent>{body}</ProductBandDisclosureContent>
 *   </ProductBandDisclosure>
 *
 * Composed over `@vs-shop/ui`'s collapsible rather than rebuilt, which is what
 * makes the open state a fact of the DOM rather than a prop threaded down: the
 * primitive marks the trigger while its panel is open, and the chevron turns
 * off that mark. So none of these parts takes an `isOpen`, and a tenant that
 * reorders them or drops the chevron changes nothing about the wiring.
 *
 * These parts are plain components. The primitive underneath them is a client
 * one, and `open`/`onOpenChange` are a caller's handlers, so the composition
 * that holds them is a tenant's client component — the body it puts inside
 * reaches the panel as `children` and stays server-rendered.
 *
 * Nothing here owns an element id. The primitive wires the trigger to its own
 * panel; a page adding a region role or a label points at ids it minted itself,
 * through the native prop spread.
 */

/**
 * The folded band.
 *
 * Closed off from whatever follows it by a rule of its own, because a stack of
 * folded bands is a list of titles and nothing else would separate them. It
 * stands down at the width its open sibling takes over.
 */
const productBandDisclosureClasses =
  'border-foreground/10 lap:hidden flex flex-col gap-4 border-b py-5';

export type ProductBandDisclosureProps = ComponentProps<typeof Collapsible>;

/** The band, folded. */
export const ProductBandDisclosure = ({ className, ...props }: ProductBandDisclosureProps) => (
  <Collapsible
    data-slot="product-band-disclosure"
    className={clsxm(productBandDisclosureClasses, className)}
    {...props}
  />
);

/**
 * The control that opens the band.
 *
 * The whole title row is the control rather than a chevron beside it, which is
 * what makes a band openable by a thumb. It is the hover and open SCOPE its
 * contents key off — structure a composition cannot see, and load-bearing when
 * missing, so it belongs to the part rather than to whoever composes it.
 */
const productBandDisclosureTriggerClasses = 'group';

export type ProductBandDisclosureTriggerProps = ComponentProps<typeof CollapsibleTrigger>;

/** The band's title row, as a control. */
export const ProductBandDisclosureTrigger = ({
  className,
  ...props
}: ProductBandDisclosureTriggerProps) => (
  <CollapsibleTrigger
    data-slot="product-band-disclosure-trigger"
    className={clsxm(productBandDisclosureTriggerClasses, className)}
    {...props}
  />
);

/**
 * What the control holds: the band's title at one end, the mark of its state at
 * the other.
 *
 * Its own part rather than the trigger's inner element, because a tenant that
 * puts a count or a mark beside the title composes it here.
 */
const productBandDisclosureHeaderClasses = 'flex items-center justify-between gap-x-4';

export type ProductBandDisclosureHeaderProps = ComponentProps<'div'>;

/** The control's contents, laid out. */
export const ProductBandDisclosureHeader = ({
  className,
  ...props
}: ProductBandDisclosureHeaderProps) => (
  <div
    data-slot="product-band-disclosure-header"
    className={clsxm(productBandDisclosureHeaderClasses, className)}
    {...props}
  />
);

/**
 * The mark that says which way the band is.
 *
 * It turns off the primitive's own open marker on the trigger above it rather
 * than off a prop, so it is correct at first paint on the server and stays
 * correct without the state ever crossing into this file. Hidden from assistive
 * technology: the control it sits in already announces its own expanded state,
 * and a second announcement of the same fact is noise.
 */
const productBandDisclosureChevronClasses =
  'size-6 shrink-0 transition-transform group-data-[panel-open]:rotate-180';

export type ProductBandDisclosureChevronProps = ComponentProps<typeof ChevronDown>;

/** The open/closed mark. */
export const ProductBandDisclosureChevron = ({
  className,
  ...props
}: ProductBandDisclosureChevronProps) => (
  <ChevronDown
    data-slot="product-band-disclosure-chevron"
    aria-hidden="true"
    className={clsxm(productBandDisclosureChevronClasses, className)}
    {...props}
  />
);

export type ProductBandDisclosureContentProps = ComponentProps<typeof CollapsibleContent>;

/**
 * What the control reveals.
 *
 * Carries no recipe: the step between it and the title is the folded band's,
 * and what sits inside is whatever the tenant composed into the open band as
 * well. The element exists so the anatomy is reachable and so the primitive has
 * a panel to wire the trigger to.
 */
export const ProductBandDisclosureContent = ({
  className,
  ...props
}: ProductBandDisclosureContentProps) => {
  // The primitive's `className` is a string or a function of its own state, and
  // under `exactOptionalPropertyTypes` it is not also `undefined` — so a panel
  // no caller styled omits the prop rather than passing an empty string, which
  // would stamp `class=""` on every band.
  const resolved = optionalClassName(clsxm(className));

  return (
    <CollapsibleContent
      data-slot="product-band-disclosure-content"
      {...(resolved === undefined ? {} : { className: resolved })}
      {...props}
    />
  );
};
